import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Trash2, Copy, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const ROLES = ["admin", "manager", "employee", "viewer"];

const ROLE_COLOR = {
  owner:    "bg-primary text-primary-foreground",
  admin:    "bg-purple-100 text-purple-800",
  manager:  "bg-blue-100 text-blue-800",
  employee: "bg-zinc-200 text-zinc-800",
  viewer:   "bg-zinc-100 text-zinc-600",
};

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", role: "employee" });
  const [tempCreds, setTempCreds] = useState(null);
  const [busy, setBusy] = useState(false);

  const canManage = user?.role === "owner" || user?.role === "admin";

  const load = () => api.get("/team/members").then((r) => setMembers(r.data));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/team/members", form);
      setTempCreds(data);
      setForm({ email: "", full_name: "", role: "employee" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/team/members/${id}`, { role });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Remove ${email} from the workspace?`)) return;
    try {
      await api.delete(`/team/members/${id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="team-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-primary">/ WORKSPACE · TEAM</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Team &amp; Roles</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage who has access to this workspace and what they can do.
          </p>
        </div>
        {canManage && (
          <Dialog open={open || !!tempCreds} onOpenChange={(o) => { if (!o) { setOpen(false); setTempCreds(null); } }}>
            <DialogTrigger asChild>
              <Button onClick={() => setOpen(true)} data-testid="invite-member-btn" className="rounded-sm bg-primary hover:bg-primary/90">
                <UserPlus className="w-4 h-4 mr-2" /> Invite member
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm" data-testid="invite-dialog">
              {tempCreds ? (
                <>
                  <DialogHeader>
                    <DialogTitle className="font-display">Member invited</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Share these credentials with <span className="font-medium text-foreground">{tempCreds.email}</span>. They will be prompted to reset their password on first login.
                    </p>
                    <div className="border border-border rounded-sm p-4 space-y-2 bg-secondary/40">
                      <div className="flex items-baseline justify-between">
                        <span className="overline text-muted-foreground">Email</span>
                        <code className="font-mono-plex text-sm">{tempCreds.email}</code>
                      </div>
                      <div className="flex items-baseline justify-between">
                        <span className="overline text-muted-foreground">Temp password</span>
                        <code className="font-mono-plex text-sm" data-testid="temp-password">{tempCreds.temp_password}</code>
                      </div>
                      <Button
                        variant="outline" size="sm" className="rounded-sm w-full mt-2"
                        onClick={() => {
                          navigator.clipboard.writeText(`${tempCreds.email} / ${tempCreds.temp_password}`);
                          toast.success("Copied");
                        }}
                        data-testid="copy-credentials"
                      >
                        <Copy className="w-3 h-3 mr-2" /> Copy credentials
                      </Button>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={() => { setTempCreds(null); setOpen(false); }} className="rounded-sm bg-primary hover:bg-primary/90">Done</Button>
                  </DialogFooter>
                </>
              ) : (
                <form onSubmit={invite}>
                  <DialogHeader>
                    <DialogTitle className="font-display">Invite team member</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 py-4">
                    <div>
                      <Label>Full name</Label>
                      <Input required data-testid="invite-name" className="rounded-sm mt-1.5"
                        value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input type="email" required data-testid="invite-email" className="rounded-sm mt-1.5"
                        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div>
                      <Label>Role</Label>
                      <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                        <SelectTrigger className="rounded-sm mt-1.5" data-testid="invite-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={busy} data-testid="invite-submit" className="rounded-sm bg-primary hover:bg-primary/90">
                      {busy ? "Inviting…" : "Invite"}
                    </Button>
                  </DialogFooter>
                </form>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {["owner", "admin", "manager", "employee"].map((r) => {
          const count = members.filter((m) => m.role === r).length;
          return (
            <div key={r} className="border border-border bg-card p-6" data-testid={`team-kpi-${r}`}>
              <div className="overline text-muted-foreground">{r}s</div>
              <div className="mt-3 font-display text-4xl font-bold">{count}</div>
            </div>
          );
        })}
      </div>

      {members.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center">
          <Users className="w-8 h-8 mx-auto text-muted-foreground" />
          <p className="mt-4 font-display text-lg">No members yet</p>
        </div>
      ) : (
        <div className="border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr className="text-left">
                <th className="px-4 py-3 overline text-muted-foreground">Name</th>
                <th className="px-4 py-3 overline text-muted-foreground">Email</th>
                <th className="px-4 py-3 overline text-muted-foreground">Role</th>
                <th className="px-4 py-3 overline text-muted-foreground">Joined</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-b border-border hover:bg-secondary/50" data-testid={`member-row-${m.id}`}>
                  <td className="px-4 py-3 font-medium">{m.full_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{m.email}</td>
                  <td className="px-4 py-3">
                    {canManage && m.role !== "owner" && m.id !== user?.id ? (
                      <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                        <SelectTrigger className="rounded-sm h-8 w-32" data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`${ROLE_COLOR[m.role]} rounded-sm font-mono-plex text-[10px] uppercase tracking-widest`}>
                        {m.role === "owner" && <ShieldCheck className="w-3 h-3 mr-1" />}
                        {m.role}
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canManage && m.role !== "owner" && m.id !== user?.id && (
                      <Button variant="ghost" size="sm" onClick={() => remove(m.id, m.email)} data-testid={`remove-${m.id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
